import { ApiError, request, usingMockApi } from './client';
import { wellsApi } from './wells';
import { STANDARD_LAYOUT_ID, isPermissionKey } from '../access/permissions';
import type {
  AdminStore,
  AdminUser,
  Company,
  CompanyStatus,
  DataSource,
  NamedLayout,
  Role,
  WellExtra,
} from '../access/permissions';
import * as sim from '../mock/admin';
import type { CompanyInput, RoleInput, UserInput, WellInput } from '../mock/admin';
import { DEFAULT_LAYOUT, sanitiseLayout } from '../nav/rails';
import type { NavLayout } from '../nav/rails';
import type { User, Well } from '../types';

/**
 * The admin area's data: companies, wells, roles, layouts and users.
 *
 * With a real API (VITE_MOCK_API=false) every function here is one call of
 * the API described in docs/api/ADMIN.md, and the server is what decides who
 * may do what. In mock mode the same functions run on lib/mock/admin.ts, which
 * keeps everything in this browser, so the pages can be built and tried with
 * nothing else started.
 *
 * Either way a function that changes something answers with the admin area as
 * it is afterwards, read again from where it is kept. The pages hand that to
 * AdminContext (`commit`) and never patch their own copy, so what is on the
 * screen is what was saved — including what the server changed on the way.
 */

/** True when the admin area lives in this browser and nowhere else. */
export const ADMIN_SIMULATED = usingMockApi;

export const { ADMIN_STORAGE_KEY, MAX_DATA_LEVEL, WELL_STATUSES } = sim;
export type { CompanyInput, RoleInput, UserInput, WellInput };

// ------------------------------------------------------------------ reading

interface Page<T> {
  items?: T[];
}

/** What the API calls a well: the portal's well, with its company and sources. */
type ServerWell = Well & { companyId?: number | null; sources?: DataSource[] };

/** An account as GET /api/users lists it; one with a fixed role has no company. */
interface ServerUser {
  id: number;
  name: string;
  email: string;
  active: boolean;
  companyId: number | null;
  roleId: number | null;
  wellIds?: number[] | null;
  createdAt: string;
  createdBy?: string;
}

const standardLayout = (): NamedLayout => ({ id: STANDARD_LAYOUT_ID, name: 'Standard', rails: DEFAULT_LAYOUT.rails });

/** Nothing loaded yet: what the pages draw until the server has answered. */
function emptyStore(): AdminStore {
  return {
    version: 1,
    seq: 0,
    companies: [],
    wells: [],
    wellExtras: {},
    layouts: [standardLayout()],
    roles: [],
    users: [],
  };
}

/**
 * The admin area as last kept in this browser — which, with a real API, is
 * nothing: the first paint draws an empty admin area and loadAdminStore fills
 * it in. Synchronous on purpose, so mock mode's first paint has the right menu.
 */
export function readAdminStore(): AdminStore {
  return ADMIN_SIMULATED ? sim.readAdminStore() : emptyStore();
}

const items = <T,>(page: Page<T> | T[] | null | undefined): T[] =>
  Array.isArray(page) ? page : Array.isArray(page?.items) ? page.items : [];

/** The person the lists are read for; kept so a save can read them again. */
let reader: User | null = null;

async function readServer(): Promise<AdminStore> {
  const me = reader;
  const store = emptyStore();
  if (!me) return store;

  // Asking only for what this person may read: the server would answer 403
  // for the rest, and a 403 is not something to shrug off in general.
  const superadmin = me.role === 'superadmin';
  const held = me.permissions ?? [];
  const managesPeople = superadmin || held.includes('users.manage') || held.includes('welladmins.manage');

  const [wells, companies, roles, users, layouts] = await Promise.all([
    request<Page<ServerWell> | ServerWell[]>('/wells'),
    managesPeople ? request<Page<Company>>('/companies') : null,
    managesPeople ? request<Page<Role>>('/roles') : null,
    managesPeople ? request<Page<ServerUser>>('/users') : null,
    superadmin ? request<Page<NamedLayout>>('/layouts') : null,
  ]);

  store.serverWells = items(wells);
  for (const well of store.serverWells as ServerWell[]) {
    const extra: WellExtra = { companyId: well.companyId ?? null, sources: well.sources ?? [] };
    store.wellExtras[String(well.id)] = extra;
  }

  store.companies = items(companies).map((c) => ({
    ...c,
    legalName: c.legalName || undefined,
    status: c.status as CompanyStatus | undefined,
  }));

  store.roles = items(roles).map((r) => ({
    ...r,
    description: r.description ?? '',
    permissions: (r.permissions ?? []).filter(isPermissionKey),
  }));

  // The accounts the admin area arranges are the ones in a company. The few
  // with a fixed role (the superadmin's own among them) are listed by the API
  // too and are not the pages' to edit.
  store.users = items(users)
    .filter((u): u is ServerUser & { companyId: number; roleId: number } => u.companyId != null && u.roleId != null)
    .map(
      (u): AdminUser => ({
        id: u.id,
        name: u.name,
        email: u.email,
        companyId: u.companyId,
        roleId: u.roleId,
        wellIds: u.wellIds ?? [],
        active: u.active,
        createdAt: u.createdAt,
        createdBy: u.createdBy ?? '',
      }),
    );

  // Only the superadmin reads the layouts; everyone else has their own, which
  // came with their profile.
  const named: NamedLayout[] = [];
  for (const raw of items(layouts)) {
    const clean = sanitiseLayout(raw);
    if (clean) named.push({ id: raw.id, name: raw.name, rails: clean.rails });
  }
  const own = me.layout && sanitiseLayout(me.layout);
  if (named.length === 0 && own && me.layout) named.push({ id: me.layout.id, name: me.layout.name, rails: own.rails });
  if (named.length > 0) store.layouts = named;

  return store;
}

/**
 * The admin area as `me` may see it, and the wells with it. Called when
 * somebody signs in; a save reads it again by itself.
 */
export async function loadAdminStore(me: User): Promise<AdminStore> {
  reader = me;
  if (!ADMIN_SIMULATED) return readServer();
  return { ...sim.readAdminStore(), serverWells: await wellsApi.list() };
}

// ------------------------------------------------------------------ writing

const send = (method: string, path: string, body?: unknown) =>
  request<unknown>(path, { method, body: body === undefined ? undefined : JSON.stringify(body) });

/** One change on the server, then the admin area as it now is. */
async function change(call: Promise<unknown>): Promise<AdminStore> {
  await call;
  return readServer();
}

const save = (path: string, id: number | null, body: unknown) =>
  change(id == null ? send('POST', path, body) : send('PUT', `${path}/${id}`, body));

/**
 * POST /api/companies (id absent) · PUT /api/companies/{id}
 *
 * `wellIds`, when given, is every well of the company: the server files the
 * listed ones that belong to nobody and lets go of the ones left out, or
 * refuses the whole save and says why.
 */
export function saveCompany(id: number | null, input: CompanyInput): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.saveCompany(id, input);
  return save('/companies', id, {
    name: input.name,
    legalName: input.legalName ?? '',
    code: input.code,
    status: input.status ?? (input.active ? 'active' : 'inactive'),
    active: input.status ? input.status === 'active' : input.active,
    wellIds: input.wellIds,
  });
}

/** DELETE /api/companies/{id} — refused while anything still belongs to it. */
export function deleteCompany(id: number): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.deleteCompany(id);
  return change(send('DELETE', `/companies/${id}`));
}

/**
 * POST /api/wells (id absent) · PUT /api/wells/{id}
 *
 * `existing` matters only to the simulation, which cannot change a well the
 * mock serves; the server replaces whichever well it is asked to.
 */
export function saveWell(
  id: number | null,
  input: WellInput,
  existing: { local: boolean; names: string[] },
): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.saveWell(id, input, existing);
  return save('/wells', id, input);
}

/** DELETE /api/wells/{id} — refused while anything is on the well. */
export function deleteWell(id: number): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.deleteWell(id);
  return change(send('DELETE', `/wells/${id}`));
}

/** POST /api/layouts — a new layout starts as a copy of another. */
export function createLayout(name: string, copyOf: number): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.createLayout(name, copyOf);
  return change(send('POST', '/layouts', { name, copyOf }));
}

/** PUT /api/layouts/{id} */
export function saveLayout(id: number, name: string, layout: NavLayout): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.saveLayout(id, name, layout);
  return change(send('PUT', `/layouts/${id}`, { name, rails: layout.rails }));
}

/** DELETE /api/layouts/{id} — never the standard one, never one a role uses. */
export function deleteLayout(id: number): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.deleteLayout(id);
  return change(send('DELETE', `/layouts/${id}`));
}

/** POST /api/roles (id absent) · PUT /api/roles/{id} */
export function saveRole(id: number | null, input: RoleInput): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.saveRole(id, input);
  return save('/roles', id, input);
}

/** DELETE /api/roles/{id} — refused while somebody holds it. */
export function deleteRole(id: number): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.deleteRole(id);
  return change(send('DELETE', `/roles/${id}`));
}

/**
 * POST /api/users (id absent) · PUT /api/users/{id}
 *
 * `context` is the simulation's: the server knows who is asking and which
 * wells the company has.
 */
export function saveUser(
  id: number | null,
  input: UserInput,
  context: { actor: string; companyWellIds: number[] },
): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.saveUser(id, input, context);
  return save('/users', id, {
    name: input.name,
    email: input.email,
    companyId: input.companyId,
    roleId: input.roleId,
    wellIds: input.wellIds,
    active: input.active,
  });
}

/** DELETE /api/users/{id} */
export function deleteUser(id: number): Promise<AdminStore> {
  if (ADMIN_SIMULATED) return sim.deleteUser(id);
  return change(send('DELETE', `/users/${id}`));
}

// ------------------------------------------------- the simulation's own

const simulationOnly = () =>
  Promise.reject<AdminStore>(new ApiError('That is only for the simulated admin area.', 400));

/** Fills an empty simulated admin area with one company and its people. */
export function loadExample(companyName: string, wellIds: number[]): Promise<AdminStore> {
  return ADMIN_SIMULATED ? sim.loadExample(companyName, wellIds) : simulationOnly();
}

/** Throws away everything the simulation holds. Never touches a server. */
export function resetAdminStore(): Promise<AdminStore> {
  return ADMIN_SIMULATED ? sim.resetAdminStore() : simulationOnly();
}
